import Anchor from "atoms/anchors/anchor";
import { ParagraphSizes } from "atoms/constants/paragraph-sizes";
import Paragraph from "atoms/typography/paragraph";
import { AppNameTm } from "constants/app-name-tm";
import UserConfigurationErrorKeys from "constants/user-configuration-error-keys";
import { MetaTagTypes } from "models/interfaces/header-data";
import FrozenSubscriptionsAlert from "molecules/frozen-subscriptions-alert/frozen-subscriptions-alert";
import StructuredData from "molecules/structured-data/structured-data";
import SubscriptionType from "organisms/enums/subscription-type";
import FullScreenTransition from "organisms/full-screen-transition/full-screen-transition";
import SubscriptionCards from "organisms/subscription-cards/subscription-cards";
import React, { useEffect, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { useHeaderData } from "utilities/contexts/use-header-data-context";
import useSubscriptionCards from "utilities/hooks/domain/subscriptions/use-subscription-cards";
import usePlansStructuredData from "utilities/hooks/structured-data/use-plans-structured-data";
import useConfigurableAlertMessages from "utilities/hooks/use-configurable-alert-messages";
import { useLocalization } from "utilities/hooks/use-localization";
import useUnauthorizedResult from "utilities/hooks/use-unauthorized-result";
import LocalizationUtils from "utilities/localization-utils";
import { RouteUtils } from "utilities/route-utils";
import { ScrollUtils } from "utilities/scroll-utils";
import StringUtils from "utilities/string-utils";
import { siteMap } from "internal-sitemap";
import SelectPlanPageHeader from "pages/registration/select-plan-page-header";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import useRefreshIdentity from "utilities/hooks/use-refresh-identity";
import UserLoginService from "utilities/services/user-logins/user-login-service";
import UserConfigurationService from "utilities/services/user-configurations/user-configuration-service";

interface SelectPlanPageProps {}

const BASE_CLASS_NAME = "c-select-plan-page";

const FREE_TRIAL_REGISTRATION_ROUTE = RouteUtils.getRegistrationPageRoute(
    SubscriptionType.FreeTrial
);

const SelectPlanPage: React.FC<SelectPlanPageProps> = () => {
    const { t } = useLocalization();
    const { setMetaTags } = useHeaderData({
        title: t("plansPage-pageTitle", { appName: AppNameTm }),
    });
    const unauthorizedResult = useUnauthorizedResult(
        UserConfigurationErrorKeys.ERROR_CURRENT_ROLE_EXPIRED
    );
    const { loading } = useCurrentIdentity();
    const { refresh: refreshIdentity } = useRefreshIdentity();
    const { isAuthenticated, globalState } = useGlobalState();
    const {
        handleBillingCycleCheck,
        handleEnterpriseCardSelect,
        handleIndividualCardSelect,
        handleTeamCardSelect,
        resetModal,
        showBillingModal,
        selectedBillingCycle,
        selectedSubscriptionType,
    } = useSubscriptionCards();
    const plansStructuredData = usePlansStructuredData();
    const { update: updateUserLoginApi } = UserLoginService.useUpdate();
    const { get: getUserConfigurationApi } = UserConfigurationService.useGet();

    const { push } = useHistory();
    const { subscriptionFreezeActive } = useConfigurableAlertMessages();

    const currentCultureCode =
        LocalizationUtils.currentCultureCode() ?? "en-US";

    const selectPlanDescriptionHtml =
        globalState.systemSettings?.subscriptionContentSettings
            ?.plansDescription[currentCultureCode] ?? "";

    const isCurrentRoleExpiredFreeTrial =
        globalState.currentIdentity?.isCurrentRoleExpiredFreeTrial();

    const [hasExpiredCurrentRoleError, setHasExpiredCurrentRoleError] =
        useState<boolean>(
            unauthorizedResult.hasErrorFor(
                UserConfigurationErrorKeys.ERROR_CURRENT_ROLE_EXPIRED
            )
        );

    const [identityLoading, setIdentityLoading] = useState<boolean>(false);
    const [identityLoaded, setIdentityLoaded] = useState<boolean>(false);

    useEffect(function initializeScrollPosition() {
        ScrollUtils.scrollToTop();
    }, []);

    useEffect(() => {
        setMetaTags([
            {
                name: MetaTagTypes.Title,
                content: `${AppNameTm}: Select a Plan to Get Started`,
            },
            {
                name: MetaTagTypes.Description,
                content: StringUtils.stripHtmlTags(selectPlanDescriptionHtml),
            },
        ]);
    }, [selectPlanDescriptionHtml, setMetaTags]);

    //Force refresh a user who recently bought a subscription and has an expired trial
    useEffect(() => {
        refreshIdentity(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (identityLoading && !loading) {
            setIdentityLoaded(true);
        }
        setIdentityLoading(loading);
    }, [identityLoading, loading]);

    // Listen for changes on the unauthorizedResult to see if the current role has expired error comes back.
    // We'll want to save this in state and show specific text to the user.
    useEffect(() => {
        if (hasExpiredCurrentRoleError) {
            return;
        }

        if (
            unauthorizedResult.doesNotHaveErrors() ||
            !unauthorizedResult.hasErrorFor(
                UserConfigurationErrorKeys.ERROR_CURRENT_ROLE_EXPIRED
            )
        ) {
            return;
        }
        setHasExpiredCurrentRoleError(true);
    }, [
        hasExpiredCurrentRoleError,
        setHasExpiredCurrentRoleError,
        unauthorizedResult,
    ]);

    // Use effect to cover the edge case where a free trial user is stuck in their old role
    // This occurs because we do not soft delete free trials because they are internalized (not part of NFPA sync)
    useEffect(() => {
        // TODO - if confirmed as a fix, pull out into hook because shares logic with the switch-profile-modal action
        const switchToActiveRole = async () => {
            const userRoles =
                globalState?.currentIdentity?.getActiveUserRoles();

            if (userRoles) {
                setIdentityLoading(true);
                try {
                    const dto = globalState.currentIdentity?.userLogin?.with({
                        roleId: userRoles[0].roleId, // Grabs the first role available, which will be the active, highest priority role
                        userRoleId: userRoles[0].id,
                    });
                    await updateUserLoginApi(dto!);

                    // Note: The order of these calls is important.
                    // getUserConfigurationApi may result in a redirect prior to refreshing the identity.
                    // We need to first ensure the currentIdentity is updated prior to calling getUserConfigurationApi, which may result in a redirect.
                    await refreshIdentity(true);

                    // This calls the userconfiguration api and it appears that we don't do anything with the response.
                    // However, the service-utils catches unauthorizedResult and sets the global state.
                    await getUserConfigurationApi({});

                    // Bring them to their dashboard
                    push(siteMap.dashboards.user);
                } catch (e) {
                    console.log(e);
                }
            }
        };
        if (
            globalState.currentIdentity?.hasActiveSubscription() &&
            isCurrentRoleExpiredFreeTrial
        ) {
            switchToActiveRole();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identityLoaded]);

    if (identityLoading) {
        return <FullScreenTransition transitionText={t("loading")} />;
    }

    if (!isAuthenticated && !identityLoading) {
        return <Redirect to={{ pathname: siteMap.home, hash: "plans" }} />;
    }

    return (
        <>
            {subscriptionFreezeActive ? (
                <FrozenSubscriptionsAlert />
            ) : (
                <React.Fragment>
                    <StructuredData jsonLd={plansStructuredData} />
                    <div className={BASE_CLASS_NAME}>
                        <SelectPlanPageHeader
                            hasExpiredCurrentRoleError={
                                hasExpiredCurrentRoleError
                            }
                            isCurrentRoleExpiredFreeTrial={
                                isCurrentRoleExpiredFreeTrial
                            }
                            selectPlanDescriptionHtml={
                                selectPlanDescriptionHtml
                            }
                        />
                        <SubscriptionCards
                            cssClassName={BASE_CLASS_NAME}
                            handleEnterpriseCardSelect={
                                handleEnterpriseCardSelect
                            }
                            handleIndividualCardSelect={
                                handleIndividualCardSelect
                            }
                            handleTeamCardSelect={handleTeamCardSelect}
                            resetModal={resetModal}
                            handleBillingCycleCheck={handleBillingCycleCheck}
                            selectedBillingCycle={selectedBillingCycle}
                            selectedSubscriptionType={selectedSubscriptionType}
                            showBillingModal={showBillingModal}
                        />
                        <div className={`${BASE_CLASS_NAME}__footer`}>
                            {!globalState?.currentIdentity?.hasHadFreeTrial() && (
                                <Paragraph size={ParagraphSizes.Large}>
                                    {t("plansPage-interestedInTryingLink", {
                                        appName: AppNameTm,
                                    })}
                                    &nbsp;
                                    <Anchor to={FREE_TRIAL_REGISTRATION_ROUTE}>
                                        {t("plansPage-freeTrial-callToAction", {
                                            appName: AppNameTm,
                                        })}
                                    </Anchor>
                                </Paragraph>
                            )}
                        </div>
                    </div>
                </React.Fragment>
            )}
        </>
    );
};

export default SelectPlanPage;
